/**
 * WHERE the listing detail page's contact control lives — ONE breakpoint,
 * stated once, for both halves of a single either/or (LET-120).
 *
 * THE DEFECT. The detail page renders the contact control twice: a card in
 * the desktop sidebar, and a bar at the bottom edge on a phone. Exactly one
 * of them should ever be on screen. Each half chose its own breakpoint by
 * hand — the sidebar column was never hidden at all, and the bar was
 * `md:hidden` — so the two halves disagreed about where the switch happens
 * and nothing in the codebase could notice:
 *
 *   * below 768 px: BOTH rendered (a bar over an inline card two screens up);
 *   * **768–1023 px: NEITHER was in view** — the bar was hidden at `md`, the
 *     sidebar did not become a sidebar until `lg`, so it sat ≥1700 px down
 *     the page. Measured on a 820×1180 iPad and at 1023×768: no contact CTA
 *     anywhere in the initial viewport of the page whose entire purpose is
 *     to produce one.
 *
 * That band existed because "hidden below X" and "hidden above Y" were two
 * independent literals in two JSX attributes. There was no artefact that
 * could be wrong — which is why nobody was.
 *
 * THE FIX IS THE TYPE. `placement()` below is generic in the breakpoint, and
 * BOTH class strings are template-literal types over that same variable, so
 * a pair naming two different breakpoints does not COMPILE:
 *
 * ```ts
 * placement({ at: 'lg', sidebar: 'hidden lg:block', bar: 'md:hidden' })
 * //                                                     ^ Type '"md:hidden"'
 * //                                is not assignable to type '"lg:hidden"'.
 * ```
 *
 * The gap is therefore unrepresentable rather than merely closed, and
 * `contact-placement.test.ts` pins the same relation at run time — because a
 * type error is invisible to a reader skimming the diff and an assertion is
 * not.
 *
 * WHERE THIS FILE LIVES, and why that is a choice rather than a constraint.
 * It sits beside `ContactListerButton` — the component whose placement it
 * decides — rather than beside `detail.tsx`, the page that spends it. Nothing
 * FORCED that. `@letsgethome/element-types` compiles a single `*` to one path
 * SEGMENT (`[^/]*`), so its page pattern — `src/features/`, one star, then
 * `/pages/` and a double-star — matches `src/features/<x>/pages/…` and never
 * `src/features/storefront/listings/pages/…`: the whole `features/storefront`
 * subtree is unclassified and therefore outside the DAG entirely. The `pages`
 * directory would have been accepted. The reason for this location is the
 * ordinary one — a constant that decides where a control renders belongs next
 * to the control.
 *
 * WHY LITERALS AND NOT A TEMPLATE. Tailwind v4 scans SOURCE TEXT for class
 * candidates (`@source`-driven, see `globals.css`). A composed
 * `` `${bp}:hidden` `` is a string Tailwind never sees, so the utility is
 * never emitted and the class silently does nothing — exactly the failure
 * mode of the `pb-safe` that `@letsgethome/ui/z-layers` documents. The
 * literals are the payload; `at` is the discriminant that welds them.
 */

/** Tailwind's default responsive scale — the only prefixes that exist. */
type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * The two class strings of one either/or, welded to a single breakpoint `B`.
 *
 * `sidebar` is display-NONE below `B` and a block from `B` up; `bar` is the
 * exact complement. Read together they partition every viewport width into
 * "sidebar" or "bar", with no overlap and — the part that was broken — no
 * gap.
 */
type ContactPlacement<B extends Breakpoint> = {
  /** The single breakpoint both strings must name. */
  readonly at: B;
  /** The sidebar column: hidden below `B`. */
  readonly sidebar: `hidden ${B}:block`;
  /** The bottom action bar: hidden from `B` up. */
  readonly bar: `${B}:hidden`;
};

/** Infers `B` from `at` and holds both class strings to it. */
function placement<B extends Breakpoint>(p: ContactPlacement<B>): ContactPlacement<B> {
  return p;
}

/**
 * The ONE breakpoint at which the contact control moves from the bottom bar
 * into the sidebar.
 *
 * `lg` and not `md`: the sidebar is a real column only at `lg` (the page grid
 * is `lg:grid-cols-3`), so switching earlier would put the card back in the
 * document flow far below the fold — which is precisely the state the
 * 768–1023 px band was in. The bar covers everything below that, including
 * the tablet widths that had nothing.
 */
export const CONTACT_PLACEMENT = placement({
  at: 'lg',
  sidebar: 'hidden lg:block',
  bar: 'lg:hidden',
});
